import {
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ElementRef,
    Input,
    inject,
    Output,
    EventEmitter,
} from '@angular/core';
import { ConfirmationPopupService, ModalDialogActive, MultiSubscriptionComponent } from '@ddv/common-components';
import { DashboardGroup, DashboardService, DashboardGroupsService, DashboardSharingService } from '@ddv/dashboards';
import { Entitlement, UserEntitlements } from '@ddv/entitlements';
import { DashboardSnapshot, getDeleteDashboardDialogOptions } from '@ddv/models';
import { combineLatest, concat, Observable, of, switchMap } from 'rxjs';

import { mapDashboardsToGroups } from '../../utils';

@Component({
    selector: 'app-dashboard-finder',
    templateUrl: 'dashboard-finder.component.html',
    styleUrls: ['dashboard-finder.component.scss'],
    standalone: false,
})
export class DashboardFinderComponent extends MultiSubscriptionComponent implements OnInit, OnDestroy {
    @Input() clientCode: string | undefined;
    @Input() entitlements: UserEntitlements | undefined;
    @Output() refetchViewTiles = new EventEmitter<void>();

    private readonly activeModal = inject(ModalDialogActive<void>);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly confirmationService = inject(ConfirmationPopupService);
    private readonly dashboardService = inject(DashboardService);
    private readonly dashboardGroupsService = inject(DashboardGroupsService);
    private readonly el = inject(ElementRef<HTMLElement>);
    private readonly sharingService = inject(DashboardSharingService);

    searchParam = '';
    filteredDashboardGroupsList: DashboardGroup[] = [];
    isFiltered = false;
    isApplyButtonEnabled = false;
    showDetails: Record<string, boolean> = {};
    canManageViewGroups = false;
    showSelectedViews = false;

    protected isLoading = false;
    protected showFamilyToggle: Record<string, boolean> = {};
    protected dashboardGroupsList: DashboardGroup[] = [];
    protected canDeleteDashboard = false;
    protected isSharedByDashboardId: Record<string, boolean> = {};

    private changedDashboardGroupList: DashboardGroup[] = [];

    ngOnInit(): void {
        this.getAllDashboards();

        this.canManageViewGroups = !!this.entitlements?.hasPermission(Entitlement.VIEW_GROUP_MANAGE);
        this.canDeleteDashboard = !!this.entitlements?.hasPermission(Entitlement.DASHBOARD_DELETE);
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();

        this.dashboardGroupsList = [];
        this.changedDashboardGroupList = [];
        this.showDetails = {};
    }

    filterSearchResults(): void {
        this.isFiltered = false;
        this.searchParam = this.searchParam ? this.searchParam.trim() : '';

        let dashboardGroups = this.dashboardGroupsList.map((dashboardGroup) => {
            const dashboards = dashboardGroup.dashboards.map((dashboard) => new DashboardSnapshot(dashboard));
            return this.createDashboardGroup(
                dashboardGroup.id,
                dashboardGroup.name,
                dashboardGroup.sortOrder,
                !!dashboardGroup.isDefault,
                dashboards);
        }) || [];

        this.isFiltered = true;
        dashboardGroups = dashboardGroups.filter((dashboardGroup) => {
            if (dashboardGroup.dashboards.length) {
                return dashboardGroup;
            }

            return;
        });

        if (this.searchParam) {
            this.isFiltered = true;
            const normalizedSearchTerm = this.searchParam.toLowerCase();
            dashboardGroups = dashboardGroups.filter((dashboardGroup) => {
                dashboardGroup.dashboards = dashboardGroup.dashboards.filter((dashboard) => {
                    return dashboard.name?.toLowerCase().includes(normalizedSearchTerm) ||
                        ('global'.includes(normalizedSearchTerm) && dashboard.isGlobal);
                });

                if (dashboardGroup.dashboards.length) {
                    return dashboardGroup;
                }

                return;
            });
        }

        this.filteredDashboardGroupsList = dashboardGroups;

        this.filterBy();
    }

    removeDashboard(dashboardId: string | number | undefined): void {
        if (!dashboardId) {
            return;
        }

        const dashboardIdAsString = dashboardId.toString();
        let snapshot: DashboardSnapshot | undefined;
        this.dashboardGroupsList.forEach((dashboardGroup) => {
            const db = dashboardGroup.dashboards.find((dashboard) => dashboard.id?.toString() === dashboardIdAsString);
            if (db) {
                snapshot = db;
                return;
            }
        });

        if (!snapshot) {
            return;
        }

        this.sharingService.isDashboardShared(this.clientCode ?? '', dashboardId)
            .subscribe({
                next: (isShared) => {
                    const confirmDialogOptions = getDeleteDashboardDialogOptions(isShared);
                    this.confirmationService.showConfirmationPopup(confirmDialogOptions)
                        .subscribe({
                            next: (action) => {
                                if (action === 'confirm') {
                                    this.onDashboardRemoval(Number(dashboardId));
                                }
                            },
                        });
                },
            });
    }

    findCurrentDashboardGroup(name: string): DashboardGroup | undefined {
        return this.dashboardGroupsList.find((dashboardGroup) => dashboardGroup.name === name);
    }

    changeDashboardFamily(
        { oldDbFamily, newDbFamily, dashboard }: { oldDbFamily: DashboardGroup, newDbFamily: DashboardGroup, dashboard: DashboardSnapshot },
    ): void {
        const dbIndex = oldDbFamily.dashboards.findIndex((db) => db.id === dashboard.id);
        oldDbFamily.dashboards.splice(dbIndex, 1);
        newDbFamily.dashboards.push(dashboard);
        newDbFamily.dashboards.sort((dbA, dbB) => dbA.name.localeCompare(dbB.name));
        this.fillChangedGroupList(oldDbFamily);
        this.fillChangedGroupList(newDbFamily);

        const oldDbFamilyHasDefaultViews = oldDbFamily.dashboards.some((db) => db.isDefault);

        if (oldDbFamily.isDefault && (oldDbFamily.dashboards.length === 0 || !oldDbFamilyHasDefaultViews)) {
            oldDbFamily.isDefault = false;
        }

        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        if (!newDbFamily.isDefault) {
            newDbFamily.isDefault = true;
        }

        this.filterSearchResults();
    }

    toggleDetails(id?: string | number): void {
        if (!id) {
            return;
        }

        this.showDetails[id] = !this.showDetails[id];

        if (this.showDetails[id]) {
            this.sharingService.isDashboardShared(this.clientCode ?? '', id)
                .subscribe({
                    next: (isShared) => this.isSharedByDashboardId[id] = isShared,
                });
        }
    }

    toggleDefault(dashboard: DashboardSnapshot, dashboardGroup: DashboardGroup): void {
        this.changeDefault(dashboard, !dashboard.isDefault, dashboardGroup);

        if (dashboardGroup.name && this.clientCode) {
            if (dashboardGroup.dashboards.every((db) => !db.isDefault)) {
                this.onGroupDefaultToggle(this.clientCode, dashboardGroup, false);
            } else if (dashboardGroup.dashboards.some((db) => db.isDefault) && !dashboardGroup.isDefault) {
                this.onGroupDefaultToggle(this.clientCode, dashboardGroup, true);
            }
        }
    }

    toggleFamilyModal(id?: string | number): void {
        if (!id) {
            return;
        }
        const element = this.el.nativeElement.querySelector(`.view-${id}`);
        element?.scrollIntoView({ behavior: 'smooth' });
        this.showFamilyToggle[id] = !this.showFamilyToggle[id];
    }

    onCancel(): void {
        this.activeModal.close();
    }

    onApply(): void {
        this.isApplyButtonEnabled = false;

        const apiCalls = this.changedDashboardGroupList
            .map((changedDashboardGroup) => {
                if (changedDashboardGroup.id && changedDashboardGroup.dashboards.length) {
                    return this.dashboardGroupsService.updateDashboardGroup(changedDashboardGroup);
                }

                if (changedDashboardGroup.id && changedDashboardGroup.dashboards.length === 0) {
                    return this.dashboardGroupsService.deleteDashboardGroup(changedDashboardGroup.id);
                }

                if (!changedDashboardGroup.id && changedDashboardGroup.dashboards.length) {
                    return this.dashboardGroupsService.addDashboardGroup(changedDashboardGroup);
                }

                return of(undefined);
            })
            .filter((call) => call !== undefined);

        concat(...apiCalls)
            .subscribe({
                next: (group: DashboardGroup | void) => {
                    if (group) {
                        if (this.dashboardGroupsList.findIndex((dbGroup) => !dbGroup.id && dbGroup.name === group.name)) {
                            const g = this.dashboardGroupsList.find((dbGroup) => dbGroup.name === group.name);
                            if (g) {
                                g.id = group.id;
                            }
                        }

                        if (this.clientCode) {
                            this.onGroupDefaultToggle(this.clientCode, group, !!group.isDefault);
                        }
                    }
                },
                error: () => {
                    this.isApplyButtonEnabled = true;
                    this.refetchViewTiles.emit();
                },
                complete: () => {
                    this.activeModal.close();
                    this.refetchViewTiles.emit();
                },
            });
    }

    toggleSelectedViews(areSelectedViewsToggled: boolean): void {
        this.showSelectedViews = areSelectedViewsToggled;
        this.filterSearchResults();
    }

    filterBy(): void {
        if (this.showSelectedViews) {
            this.filterByFavorites(this.filteredDashboardGroupsList);
        } else {
            this.filterAlphabetically(this.filteredDashboardGroupsList);
        }
    }

    filterAlphabetically(dbgList: DashboardGroup[]): void {
        dbgList.forEach((dashboardGroup) => {
            dashboardGroup.dashboards.sort((dbA, dbB) => {
                return dbA.name.localeCompare(dbB.name);
            });
        });
    }

    filterByFavorites(dbgList: DashboardGroup[]): void {
        this.filteredDashboardGroupsList = dbgList.filter((dashboardGroup) => {
            dashboardGroup.dashboards = dashboardGroup.dashboards.filter((dashboard) => dashboard.isDefault);
            if (dashboardGroup.dashboards.length) {
                return dashboardGroup;
            }

            return;
        });
    }

    protected toggleGroupDefault(dashboardGroup: DashboardGroup): void {
        if (!this.clientCode) {
            return;
        }

        const isDefault = !dashboardGroup.isDefault;
        this.onGroupDefaultToggle2(this.clientCode, dashboardGroup, isDefault)
            .pipe(
                switchMap(() => {
                    return combineLatest(dashboardGroup.dashboards.map((dashboard) => {
                        return this.changeDefault2(dashboard, isDefault, dashboardGroup);
                    }));
                }),
            )
            .subscribe({
                next: () => {
                    this.refetchViewTiles.emit();
                },
            });
    }

    private getAllDashboards(): void {
        this.isLoading = true;
        combineLatest([this.dashboardGroupsService.getDashboardGroups(), this.dashboardService.fetchAllDashboards(this.clientCode ?? '')])
            .subscribe({
                next: ([dashboardGroups, dashboardSnapshots]) => {
                    const dashboardsInGroups: Set<string | number> = new Set();
                    const groupDashboards: DashboardSnapshot[] = [];

                    const groupsWithMappedDashboards = mapDashboardsToGroups(dashboardGroups, dashboardSnapshots);
                    groupsWithMappedDashboards.forEach((group) => {
                        group.dashboards.forEach((db) => {
                            dashboardsInGroups.add(db.id ?? '');
                            groupDashboards.push(db);
                        });
                    });

                    dashboardSnapshots.forEach((dashboard) => {
                        this.showDetails[dashboard.id ?? ''] = false;
                        this.showFamilyToggle[dashboard.id ?? ''] = false;
                    });

                    const nonGroupDashboards = dashboardSnapshots.filter((dashboard) => {
                        if (dashboardsInGroups.has(dashboard.id ?? '')) {
                            dashboardsInGroups.delete(dashboard.id ?? '');
                            const g = groupDashboards.find((db) => db.id === dashboard.id);
                            if (g) {
                                g.isDefault = dashboard.isDefault;
                            }
                        } else {
                            return dashboard;
                        }

                        return;
                    });

                    // The following lines are here, because we sometimes receive wrong dashboards in groups from the
                    // backend, so we have to filter them somehow.
                    // Once we fix the backend issue, we would not need this code.
                    groupsWithMappedDashboards.forEach((dbGroup) => {
                        dbGroup.dashboards = dbGroup.dashboards.filter((dashboard) => {
                            if (!dashboardsInGroups.has(dashboard.id ?? '')) {
                                return dashboard;
                            } else {
                                dashboardsInGroups.delete(dashboard.id ?? '');
                            }
                            return;
                        });
                    });

                    this.dashboardGroupsList = groupsWithMappedDashboards.length > 1 ?
                        [...groupsWithMappedDashboards.sort((dbgA, dbgB) => dbgA.name.localeCompare(dbgB.name))] :
                        [...groupsWithMappedDashboards];

                    this.dashboardGroupsList.forEach((dbGroup) => {
                        const hasDefaultViews = dbGroup.dashboards.some((dashboard) => dashboard.isDefault);

                        if (this.clientCode) {
                            if (dbGroup.isDefault && !hasDefaultViews) {
                                this.onGroupDefaultToggle(this.clientCode, dbGroup, false);
                            } else if (!dbGroup.isDefault && hasDefaultViews) {
                                this.onGroupDefaultToggle(this.clientCode, dbGroup, true);
                            }
                        }
                    });

                    if (nonGroupDashboards.length) {
                        const otherDashboardsGroup = this.createDashboardGroup(0, '', -1, false, nonGroupDashboards);
                        this.dashboardGroupsList.push(otherDashboardsGroup);
                    }

                    this.filterSearchResults();
                    this.isLoading = false;
                },
            });
    }

    private onDashboardRemoval(dashboardId: number): void {
        this.isLoading = true;
        this.filteredDashboardGroupsList = [];
        this.changeDetectorRef.detectChanges();

        this.dashboardService.deleteDashboard(this.clientCode ?? '', dashboardId).subscribe({
            next: () => {
                this.refetchViewTiles.emit();
                this.getAllDashboards();
            },
        });
    }

    private createDashboardGroup(
        id: string | number,
        name: string,
        sortOrder: number, // [GROUPS] deprecated - delete when we no longer support groups from mw
        isDefault: boolean,
        dashboards: DashboardSnapshot[],
    ): DashboardGroup {
        return new DashboardGroup({ id, name, sortOrder, isDefault, dashboards });
    }

    private fillChangedGroupList(family: DashboardGroup): void {
        if (family.name) {
            const index = this.changedDashboardGroupList.findIndex((group) => group.name === family.name);
            if (index !== -1) {
                this.changedDashboardGroupList.splice(index, 1, family);
            } else {
                this.changedDashboardGroupList.push(family);
            }
        }
    }

    private changeDefault(dashboard: DashboardSnapshot, isDefault: boolean, dashboardGroup: DashboardGroup): void {
        const openOrCloseDashboard$ = isDefault ?
            this.dashboardService.openDashboard(this.clientCode ?? '', dashboard.id ?? 0) :
            this.dashboardService.closeDashboard(this.clientCode ?? '', dashboard.id ?? 0);
        openOrCloseDashboard$.subscribe({
            next: () => {
                this.refetchViewTiles.emit();
            },
        });
        dashboard.isDefault = isDefault;
        const g = this.dashboardGroupsList.find((group) => group.name === dashboardGroup.name);
        const d = g?.dashboards.find((db) => db.id === dashboard.id);
        if (d) {
            d.isDefault = isDefault;
        }
    }

    private changeDefault2(dashboard: DashboardSnapshot, isDefault: boolean, dashboardGroup: DashboardGroup): Observable<void> {
        dashboard.isDefault = isDefault;
        const g = this.dashboardGroupsList.find((group) => group.name === dashboardGroup.name);
        const d = g?.dashboards.find((db) => db.id === dashboard.id);
        if (d) {
            d.isDefault = isDefault;
        }

        const openOrCloseDashboard$ = isDefault ?
            this.dashboardService.openDashboard(this.clientCode ?? '', dashboard.id ?? 0) :
            this.dashboardService.closeDashboard(this.clientCode ?? '', dashboard.id ?? 0);

        return openOrCloseDashboard$;
    }

    private onGroupDefaultToggle(clientCode: string, dashboardGroup: DashboardGroup, isDefault: boolean): void {
        dashboardGroup.isDefault = isDefault;
        const g = this.dashboardGroupsList.find((group) => group.name === dashboardGroup.name);
        if (g) {
            g.isDefault = isDefault;
        }

        // i have no idea what crazy logic/models get us here, but without this check
        // you cannot toggle more than one group without closing and reopening the dialog
        if (dashboardGroup.id) {
            this.dashboardGroupsService.toggleDefault(clientCode, dashboardGroup.id, dashboardGroup.isDefault)
                .subscribe({
                    next: () => {
                        this.refetchViewTiles.emit();
                    },
                });
        }
    }

    private onGroupDefaultToggle2(clientCode: string, dashboardGroup: DashboardGroup, isDefault: boolean): Observable<unknown> {
        dashboardGroup.isDefault = isDefault;
        const g = this.dashboardGroupsList.find((group) => group.name === dashboardGroup.name);
        if (g) {
            g.isDefault = isDefault;
        }

        // i have no idea what crazy logic/models get us here, but without this check
        // you cannot toggle more than one group without closing and reopening the dialog
        if (dashboardGroup.id) {
            return this.dashboardGroupsService.toggleDefault(clientCode, dashboardGroup.id, dashboardGroup.isDefault);
        }

        return of(undefined);
    }
}
